<template>
  <div class="szw-box-border szw-relative szw-overflow-hidden szw-touch-pan-x szw-w-full" @mouseenter="pauseAutoPlay" @mouseleave="resumeAutoPlay">
    <div v-if="hasControls && !isSmallWidgetContainerSize" class="szw-flex szw-absolute szw-h-full szw-flex-row szw-w-full">
      <!-- control left -->
      <div :class="[classes, controlsClasses.wrapper, 'szw-left-0 szw-top-1/2 szw-z-[2]']" :style="{ transform: 'translateY(-50%)' }" @click="move('left')">
        <sprite sprite-id="arrow-up" :color="controlsClasses.stripe" style="rotate: -90deg" />
      </div>
      <!-- control right -->
      <div :class="[classes, controlsClasses.wrapper, 'szw-right-0 szw-top-1/2 szw-z-[2]']" :style="{ transform: 'translateY(-50%)' }" @click="move('right')">
        <sprite sprite-id="arrow-up" :color="controlsClasses.stripe" style="rotate: 90deg" />
      </div>
    </div>
    <div
      class="list-view szw-relative szw-overflow-hidden szw-touch-pan-x szw-w-full"
      :style="{ width: `calc(100% - ${hasControls && !isSmallWidgetContainerSize ? 96 : 16}px)`, margin: 'auto', cursor: state.isDragging ? 'grabbing' : 'grab' }"
    >
      <div
        ref="listView"
        class="horizontal-view szw-flex szw-select-none szw-whitespace-nowrap szw-w-fit szw-gap-2"
        @mousedown="startDragging"
        @mousemove="onDragging"
        @mouseup="stopDragging"
        @mouseleave="stopDragingOnLeave"
        @touchstart="startDragging"
        @touchmove="onDragging"
        @touchend="stopDragging"
      >
        <div
          v-for="match in itemsforRender"
          :key="match.uuid"
          :style="{ transform: `translateX(-${hasControls && !isSmallWidgetContainerSize ? firstItem.width + gapSize : 0}px)` }"
          class="szw-select-none szw-pointer-events-none"
        >
          <div
            v-if="match.kind === 'category'"
            :class="[isSmallWidgetContainerSize ? 'szw-h-[66px]' : 'szw-h-[76px]', 'szw-w-[100px] szw-flex szw-flex-col szw-justify-center szw-items-center']"
          >
            <img v-if="match.logo" class="szw-w-[48px] szw-h-[48px] szw-select-none szw-pointer-events-none" :src="match.logo.light.small" alt="logo" />
            <div
              v-else
              class="szw-text-xxs szw-font-semibold szw-uppercase szw-text-text-icon-label szw-tracking-[0.4px] szw-max-w-[100px] szw-truncate szw-whitespace-break-spaces szw-text-center"
            >
              {{ match.name }}
            </div>
          </div>
          <stripe-match
            v-if="match.kind === 'event'"
            :event="match"
            :statusInfo="createMatchStatus(match)"
            :is-mobile-view="isSmallWidgetContainerSize"
            :competition="match.competition.name"
            :sportType="match.sportType"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, reactive, onUnmounted, watch } from 'vue'
import { loc } from '@/__core__/localization/localization'
import { labelMonthMap } from '../Stripe.constants'
import StripeMatch from './StripeMatch.vue'
import Sprite from '@/__core__/components/sprite/Sprite.vue'
import { useStripeStore } from '../Stripe.store'

const stripeStore = useStripeStore()

const props = defineProps({
  list: {
    type: Array,
    default: () => []
  },
  isSmallWidgetContainerSize: {
    type: Boolean,
    default: false
  },
  widgetContainerWidth: {
    type: Number,
    default: 0
  },
  languageIdentifier: {
    type: String,
    default: 'eng'
  }
})

const emit = defineEmits(['has-controls'])

const items = computed(() => [...props.list])

const classes = computed(() => 'szw-text-white szw-rounded szw-h-[40px] szw-w-[40px] szw-flex szw-justify-center szw-items-center szw-absolute szw-cursor-pointer')

const controlsClasses = computed(() => ({ wrapper: 'szw-bg-latest-color-primary', stripe: 'rgb(var(--szw-color-primary))' }))

const cellSizes = computed(() => ({
  category: 100, // px for category
  event: !props.isSmallWidgetContainerSize ? 200 : 148, // px for event,
  offset: 8, // px for offset,
  parrentOffset: 16 / 2
}))

const state = reactive({
  intervalId: null,
  isDragging: false,
  startX: 0,
  currentX: 0
})

const gapSize = 8 // gapSize size px
const listView = ref(null) // Reference to the list view element
const waitForUserToRead = ref(null)
const autoPlayInterval = ref(null)
const itemsforRender = ref([])

const lastItem = computed(() => {
  return itemsforRender.value[itemsforRender.value.length - 1]
})

const firstItem = computed(() => {
  return itemsforRender.value[0]
})

const firstVisibleItem = computed(() => {
  return itemsforRender.value[1]
})

const hasControls = computed(() => {
  const size = items.value.reduce((total, item) => total + cellSizes.value[item.kind] + cellSizes.value.offset, cellSizes.value.parrentOffset)
  return props.widgetContainerWidth < size
})

const applyTransition = (element, distance, duration) => {
  element.style.transition = `transform ${duration}ms ease`
  element.style.transform = `translateX(${distance}px)`
}

const resetTransition = element => {
  element.style.transition = 'none'
  element.style.transform = 'translateX(0)'
}

const moveItemToLeft = () => {
  if (itemsforRender.value.length > 0) {
    const listViewElement = listView.value
    const itemWidth = firstItem.value.width + gapSize

    itemsforRender.value.pop() // Remove the last item
    applyTransition(listViewElement, itemWidth, 300)

    state.intervalId = setTimeout(() => {
      itemsforRender.value.unshift(lastItem.value) // Add it to the beginning
      resetTransition(listViewElement)
      clearTimeout(state.intervalId)
      state.intervalId = null
    }, 300)
  }
}

const moveItemToRight = () => {
  if (itemsforRender.value.length > 0) {
    const listViewElement = listView.value
    const itemWidth = firstVisibleItem.value.width + gapSize

    applyTransition(listViewElement, -itemWidth, 300)
    itemsforRender.value.push(firstItem.value) // Add to the end

    state.intervalId = setTimeout(() => {
      itemsforRender.value.shift() // Remove the first item
      resetTransition(listViewElement)
      clearTimeout(state.intervalId)
      state.intervalId = null
    }, 300)
  }
}

const move = direction => {
  if (!listView.value) return
  if (state.intervalId) return

  if (direction === 'left') {
    moveItemToLeft()
  } else if (direction === 'right') {
    moveItemToRight()
  }
}

const moveOnDrag = diffX => {
  if (!listView.value) return

  const listViewElement = listView.value
  const itemWidth = firstVisibleItem.value.width + gapSize
  const hiddenElWidth = firstItem.value.width

  listViewElement.style.transform = `translateX(${diffX}px)`

  if (diffX > hiddenElWidth && !state.intervalId) {
    const last = itemsforRender.value.pop() // Remove the last item
    itemsforRender.value.unshift(last) // Add it to the beginning
    state.startX += firstVisibleItem.value.width // Adjust startX after moving an item to the left
    // Reset the transform without transition once the shift is done
    listViewElement.style.transform = 'translateX(0)'
  } else if (diffX < -itemWidth && !state.intervalId) {
    const removed = itemsforRender.value.shift() // Remove the first item
    itemsforRender.value.push(removed) // Add to the end
    state.startX -= itemWidth // Adjust startX after moving an item to the right
    // Reset the transform without transition once the shift is done
    listViewElement.style.transform = 'translateX(0)'
  }
}

const startDragging = event => {
  if (state.intervalId || !hasControls.value) return

  stripeStore.setIsPaused(true)
  state.isDragging = true

  if (event.type === 'touchstart') pauseAutoPlay()

  const clientX = event.type === 'touchstart' ? event.touches[0].clientX : event.clientX

  state.startX = clientX
}

const onDragging = event => {
  if (!state.isDragging) return

  const clientX = event.type === 'touchmove' ? event.touches[0].clientX : event.clientX
  const diffX = clientX - state.startX
  moveOnDrag(diffX)
}

const stopDragingOnLeave = () => {
  stripeStore.setIsPaused(false)
  state.isDragging = false
  state.currentX = 0

  if (state.intervalId || !hasControls.value) return

  listView.value.style.transition = 'transform 100ms ease'
  listView.value.style.transform = 'translateX(0)'

  state.intervalId = setTimeout(() => {
    listView.value.style.transition = 'none'
    clearTimeout(state.intervalId)
    state.intervalId = null
  }, 100)
}

const stopDragging = event => {
  if (!listView.value || !hasControls.value) return

  state.currentX = 0
  stripeStore.setIsPaused(false)
  state.isDragging = false

  // if user or mobile swiped give him time to read swiped element
  if (event.type === 'touchend') {
    clearTimeout(waitForUserToRead.value)
    waitForUserToRead.value = null
    waitForUserToRead.value = setTimeout(() => {
      resumeAutoPlay()
      clearTimeout(waitForUserToRead.value)
      waitForUserToRead.value = null
    }, 4000)
  }

  const clientX = event.type === 'touchend' ? event.changedTouches[0].clientX : event.clientX
  const diffX = clientX - state.startX
  const itemWidth = firstVisibleItem.value.width + gapSize
  const threshold = itemWidth / 2

  if (diffX < -threshold && !state.intervalId) {
    moveItemToRight()
  } else {
    listView.value.style.transition = 'transform 100ms ease'
    listView.value.style.transform = 'translateX(0)'

    state.intervalId = setTimeout(() => {
      listView.value.style.transition = 'none'
      clearTimeout(state.intervalId)
      state.intervalId = null
    }, 100)
  }
}

const editEvent = (item, index, suffix = '') => {
  const sportMap = {
    GLOBAL_BASKETBALL: 'basketball',
    FOOTBALL: 'football'
  }

  return {
    ...item,
    uuid: `${item.id}-${index}-${suffix}`,
    traslatedSportType: loc(`sm_${sportMap[item.sport]}`),
    sportType: sportMap[item.sport],
    width: cellSizes.value[item.kind] || 0
  }
}

const initList = () => {
  pauseAutoPlay()
  if (hasControls.value && !props.isSmallWidgetContainerSize) {
    const itemsOriginal = items.value ? items.value.map((item, index) => editEvent(item, index, 'original')) : []
    const itemsDuplicated = items.value ? items.value.map((item, index) => editEvent(item, index, 'fake')) : []

    // Create an extended list by mapping over the items and creating fake items with the prefix
    const extendedList = [...itemsOriginal, ...itemsDuplicated]
    const lastElIndex = extendedList.length - 1

    // Add the last item to the beginning of the list
    extendedList.unshift(extendedList[lastElIndex])

    // Remove the last item from the list
    extendedList.pop()

    // Update the state with the first and last items and the items for rendering
    itemsforRender.value = [...extendedList]
    autoPlay()
  } else {
    props.isSmallWidgetContainerSize && autoPlay()
    itemsforRender.value = items.value ? items.value.map((item, index) => editEvent(item, index, 'all-visible')) : []
  }
}

const autoPlay = () => {
  if (autoPlayInterval.value || !hasControls.value) return

  autoPlayInterval.value = setInterval(() => {
    move('right') // Move to the right automatically
  }, 4000)
}

const pauseAutoPlay = () => {
  if (autoPlayInterval.value) {
    clearInterval(autoPlayInterval.value)
    autoPlayInterval.value = null
  }
}

const resumeAutoPlay = () => {
  if (!state.isDragging) {
    autoPlay()
  }
}

const unwatchSmallWidgetSize = watch(
  () => props.isSmallWidgetContainerSize,
  () => initList()
)

const unwatchHasControls = watch(
  () => hasControls.value,
  newValue => {
    emit('has-controls', newValue)

    return initList()
  }
)

const unwatchListUpdated = watch(
  () => items.value,
  newItemsValue => {
    newItemsValue.forEach(newItem => {
      // Find all occurrences of the item with the same id in itemsForRender
      itemsforRender.value.forEach((item, index) => {
        if (item.id === newItem.id) {
          // Update the item at the found index
          itemsforRender.value[index] = { ...item, ...newItem }
        }
      })
    })
  }
)

onMounted(() => {
  initList()
  emit('has-controls', hasControls.value)
})

onUnmounted(() => {
  // Clear timeouts and intervals
  clearTimeout(state.intervalId)
  state.intervalId = null
  clearInterval(autoPlayInterval.value)
  autoPlayInterval.value = null
  clearTimeout(waitForUserToRead.value)
  waitForUserToRead.value = null

  // Stop watchers
  unwatchListUpdated()
  unwatchSmallWidgetSize()
  unwatchHasControls()

  // Clean up DOM references if necessary
  listView.value = null
})

const createMatchStatus = event => {
  const eventDate = new Date(event.startTime)
  const currentDate = new Date()
  const isFutureDate = eventDate > currentDate
  const live = event.status.live
  const upcoming = {
    date: formatDate(event.startTime),
    time: formatTime(event.startTime),
    isUpcoming: isFutureDate
  }

  const mapStatusLabel = {
    ended: 'sm_full_time_short',
    aet: 'sm_aet',
    ap: 'sm_ap'
  }

  let translatedStatus = loc(mapStatusLabel[event.status.match], props.languageIdentifier)

  if (event.status.match === 'ap') {
    const penaltiesObj = event.periods.find(period => period.type === 'penalties')
    const resultAfterPenalties = `${penaltiesObj.score.home}:${penaltiesObj.score.away}`

    translatedStatus = `${loc(mapStatusLabel[event.status.match], props.languageIdentifier)} ${resultAfterPenalties}`
  }

  return {
    live,
    status: translatedStatus,
    event: event.status.event,
    upcoming
  }
}

const formatDate = startTime => {
  const date = new Date(startTime)
  const day = date.getDate()
  const month = date.getMonth() // getMonth returns 0-based month index
  const monthShort = loc(labelMonthMap[month], props.languageIdentifier)
  return `${day} ${monthShort}`
}

const formatTime = startTime => {
  const date = new Date(startTime)
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false // Specify 24-hour time format
  })
}
</script>

<style scoped>
.horizontal-view {
  -webkit-user-drag: none;
  -webkit-touch-callout: none;
}
/* Hide scrollbars */
.list-view::-webkit-scrollbar {
  display: none; /* Chrome, Safari, and Opera */
}

.list-view {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer and Edge */
}
</style>
